import {
  ArrowsVertical,
  Column,
  SortAscending,
  SortDescending,
} from '@carbon/react/icons';
import { VisibilityState } from '@tanstack/react-table';
import { OverflowMenu, TextInput } from '@carbon/react';
import classNames from 'classnames';
import { Toggle } from 'components/toggle/toggle';
import plur from 'plur';
import { useState } from 'react';
import { useDataTableContext } from '../context';
import styles from './table-toolbar.module.scss';

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Column visibility menu for showing/hiding table columns.
 * Displays an overflow menu with toggles for each column.
 * Includes search, sort, and show/hide all functionality.
 * Columns can opt out via `meta.toggleColumnFilter: false`.
 * @return {JSX.Element} The column visibility menu element
 */
export function ColumnVisibility() {
  const { table } = useDataTableContext();

  // Local state
  const [columnFilterText, setColumnFilterText] = useState('');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC' | null>(null);

  // Column visibility logic
  const columns = table.getAllColumns();

  // Prefer meta.label when `header` is a function (e.g. tooltip-decorated headers)
  const getColumnLabel = (col: (typeof columns)[number]): string => {
    const metaLabel = col.columnDef.meta?.label;
    if (metaLabel) return metaLabel;
    const header = col.columnDef.header;
    return typeof header === 'string' ? header : (col.id ?? '');
  };

  const orderedColumns = sortOrder
    ? [...columns].sort((a, b) => {
        const aLabel = getColumnLabel(a);
        const bLabel = getColumnLabel(b);
        return aLabel.localeCompare(bLabel) * (sortOrder === 'ASC' ? 1 : -1);
      })
    : columns;

  const filteredColumns = orderedColumns.filter((col) => {
    const colLabel = getColumnLabel(col);
    return (
      colLabel.toLowerCase().includes(columnFilterText) &&
      col.columnDef.meta?.toggleColumnFilter !== false
    );
  });

  // Only count user-toggleable columns
  const toggleableColumns = orderedColumns.filter(
    (col) => col.columnDef.meta?.toggleColumnFilter !== false
  );
  const hiddenColumnCount = toggleableColumns.filter(
    (col) => !col.getIsVisible()
  ).length;
  const visibleColumnCount = toggleableColumns.length - hiddenColumnCount;

  // Handlers
  const handleShowAll = () => {
    const newVisibility: VisibilityState = {};
    columns.forEach((col) => {
      if (col.columnDef.meta?.toggleColumnFilter !== false) {
        newVisibility[col.id] = true;
      }
    });
    table.setColumnVisibility(newVisibility);
  };

  const handleHideAll = () => {
    const newVisibility: VisibilityState = {};
    columns.forEach((col) => {
      if (col.columnDef.meta?.toggleColumnFilter !== false) {
        newVisibility[col.id] = false;
      }
    });
    table.setColumnVisibility(newVisibility);
  };

  const renderSortIcon = () => {
    if (sortOrder === 'ASC') {
      return <SortDescending className={styles.sortIcon} />;
    } else if (sortOrder === 'DESC') {
      return <SortAscending className={styles.sortIcon} />;
    } else {
      return <ArrowsVertical className={styles.sortIcon} />;
    }
  };

  return (
    <>
      {hiddenColumnCount > 0 && (
        <span className={styles.columnsCount}>
          {`${hiddenColumnCount} ${plur('column', hiddenColumnCount)} hidden`}
        </span>
      )}
      <OverflowMenu
        data-testid="column-visibility-menu"
        className={styles.overflowMenu}
        renderIcon={Column}
        iconDescription="Show/Hide columns"
        align="bottom-end"
        flipped
        size="lg"
      >
        <div className={styles.menu}>
          <p className={styles.title}>Columns ({columns.length})</p>
          <div className={styles.scrollableArea}>
            <div className={styles.shortcut}>
              <button
                data-testid="hide-all-columns"
                className={classNames(
                  visibleColumnCount
                    ? styles.ghostButton
                    : styles.ghostButtonDisabled
                )}
                onClick={handleHideAll}
              >
                Hide All
              </button>
              <button
                data-testid="show-all-columns"
                onClick={handleShowAll}
                className={classNames(
                  hiddenColumnCount
                    ? styles.ghostButton
                    : styles.ghostButtonDisabled
                )}
              >
                Show All
              </button>
              <span
                onClick={() => {
                  setSortOrder((prev) => {
                    return prev === 'ASC' ? 'DESC' : 'ASC';
                  });
                }}
              >
                {renderSortIcon()}
              </span>
            </div>

            <div className={styles.searchAreaMenu}>
              <TextInput
                id="column-search"
                labelText=""
                hideLabel
                placeholder="Search columns..."
                value={columnFilterText}
                onChange={(e) =>
                  setColumnFilterText(e.target.value.toLowerCase())
                }
              />
            </div>

            {filteredColumns.map((column) => (
              <div
                key={column.id}
                className={styles.columnItem}
                data-testid={`column-item-${column.id}`}
              >
                <p className={styles.columnName}>{getColumnLabel(column)}</p>
                <div>
                  <Toggle
                    data-testid={`column-toggle-${column.id}`}
                    isActive={column.getIsVisible()}
                    handleToggle={(visibility) =>
                      column.getToggleVisibilityHandler()({
                        target: { checked: visibility },
                      })
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </OverflowMenu>
    </>
  );
}
